import type { CatalogEvent, ProjectState } from "@livcg/schema";
import { formatEventMinute, isCardEventType } from "@livcg/schema";
import { Pencil, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { sendRaw } from "../../shared/sendRaw.js";
import { Button, ConfirmDialog, cn } from "../../ui/index.js";
import { EditEventModal, type EditPatch } from "../EditEventModal.js";
import { eventLabel, eventTone } from "../eventBadges.js";
import { useEventDelete } from "../useEventDelete.js";
import { useMatchDetail } from "../useMatchDetail.js";

/** Vertical event log — in the Control Room center column, below the Overlay
 *  controls. Newest on top; each row has inline edit + delete. */
export function EventLog({ slug, state }: { slug: string; state: ProjectState }) {
  const md = useMatchDetail(state);
  const sorted = md.sortedEvents;
  const [editing, setEditing] = useState<CatalogEvent | null>(null);

  const { requestDelete, confirmProps } = useEventDelete(slug, md);
  const saveEdit = (id: string | undefined, patch: EditPatch) => {
    if (id) void sendRaw("event.update", { slug, eventId: id, ...patch }).then(() => md.refetch());
    setEditing(null);
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-divider bg-surface/40">
      <div className="flex shrink-0 items-center justify-between border-b border-divider px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
            Event log
          </span>
          <span className="text-[11px] text-ink-faint">{sorted.length}</span>
        </div>
        <Button
          variant="secondary"
          size="xs"
          iconOnly
          aria-label="Yangilash"
          onClick={() => md.refetch()}
          className="text-ink-muted"
        >
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
        {sorted.length === 0 && (
          <span className="px-1 py-2 text-[12px] text-ink-faint">Hozircha voqea yo'q.</span>
        )}
        {sorted.map((e) => (
          <div
            key={e.id}
            className="group flex items-center gap-2 rounded-md border border-divider bg-surface-muted/40 px-2 py-1.5"
          >
            <span className="w-14 shrink-0 font-display text-[12px] font-semibold tabular-nums text-ink">
              {formatEventMinute(e.minute, e.second, e.data?.added)}ʼ
            </span>
            <span
              className={cn(
                "shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium",
                eventTone(e.type),
              )}
            >
              {eventLabel(e.type)}
            </span>
            <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink-soft">
              <span className="mr-1 font-mono text-[9px] text-ink-muted">
                {md.teamShort(e.teamId)}
              </span>
              {md.playerName(e.playerId)}
              {e.assistPlayerId && (
                <span className="text-ink-faint">
                  {e.type === "substitution" ? " → " : " · assist "}
                  {md.playerName(e.assistPlayerId)}
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              aria-label="Tahrirlash"
              onClick={() => setEditing(e)}
              className="shrink-0 text-ink-faint opacity-0 transition-opacity hover:text-accent-strong group-hover:opacity-100"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              aria-label="O'chirish"
              onClick={() => requestDelete(e)}
              className="shrink-0 text-ink-faint opacity-0 transition-opacity hover:text-danger-strong group-hover:opacity-100"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>

      {editing && (
        <EditEventModal
          initial={{
            minute: editing.minute,
            second: editing.second,
            playerId: editing.playerId,
            assistPlayerId: editing.assistPlayerId ?? null,
            added: editing.data?.added ?? 0,
          }}
          eventType={editing.type}
          players={md.teamPlayers(editing.teamId, isCardEventType(editing.type))}
          teamShort={md.teamShort(editing.teamId)}
          typeLabel={eventLabel(editing.type)}
          onClose={() => setEditing(null)}
          onSave={(patch) => saveEdit(editing.id, patch)}
        />
      )}
      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
